import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader } from "../components/Loader";

export default function Chat({ order = false }) {
  const params = useParams();
  const [sp] = useSearchParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const orderId = params.orderId;
  const otherUserId = params.userId;
  const productId = sp.get("product") || undefined;
  const [name, setName] = useState(sp.get("name") || "المحادثة");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const path = order ? `/orders/${orderId}/messages` : `/messages/${otherUserId}`;

  const load = useCallback(async () => {
    try {
      setMsgs(await api(path));
      // Mark this thread as read so the conversations badge clears.
      if (!order && otherUserId) {
        try {
          await api(`/messages/mark-seen/${otherUserId}`, { method: "PUT" });
          qc.invalidateQueries({ queryKey: ["conversations"] });
        } catch { /* non-blocking */ }
      }
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [path, order, otherUserId, qc]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    setText("");
    try {
      if (order) await api(`/orders/${orderId}/messages`, { method: "POST", body: JSON.stringify({ text: t, to_user_id: "" }) });
      else await api("/messages", { method: "POST", body: JSON.stringify({ to_user_id: otherUserId, text: t, product_id: productId }) });
      await load();
      qc.invalidateQueries({ queryKey: ["conversations"] });
    } catch (e) { toast.error(e.message); setText(t); } finally { setSending(false); }
  };

  return (
    <div className="animate-fade-in-up max-w-2xl mx-auto flex flex-col h-[calc(100vh-8rem)]">
      <PageHeader title={order ? "محادثة الطلب" : name} />
      <div className="flex-1 overflow-y-auto bg-white rounded-2xl border border-[#E8EAF2] p-4 space-y-3">
        {loading ? <Loader /> : msgs.length === 0 ? (
          <p className="text-center text-[#6B7280] py-10">ابدأ المحادثة</p>
        ) : msgs.map((m, i) => {
          const mine = m.from_user_id === user?.user_id;
          return (
            <div key={m.message_id || i} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${mine ? "bg-[#FF3D5A] text-white rounded-bs-sm" : "bg-[#F0F2FA] text-[#161629] rounded-be-sm"}`}>
                {m.voice_base64 ? <audio controls src={m.voice_base64} className="max-w-full" /> : <p className="whitespace-pre-wrap break-words">{m.text}</p>}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex items-center gap-2 mt-3">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="اكتب رسالة..." className="flex-1 bg-white rounded-2xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A]" />
        <button data-testid="chat-send" disabled={sending} onClick={send} className="w-12 h-12 rounded-2xl bg-[#FF3D5A] text-white flex items-center justify-center disabled:opacity-60"><Send size={20} /></button>
      </div>
    </div>
  );
}
